import { createPcApp } from "./_app";
import { createCOLS } from "_Main/page/cols";
import { ServiceController } from "Util/serviceController";
import { inject, ref, onMounted, computed } from "vue";
import { userHasPrivilege, userHasAdminPrivilege, mapLoggedInUserData } from "Util/clientUser";
import {
    KEYS as enableMfADialogKeys,
    NAMESPACE as enableMfADialogNameSpace
} from "Components/page/i18n/enableMfADialog.i18n";
import { LOCATION_UTIL } from "Util/locationUtil";
import { GlobalEventBus } from "Util/emitter";
import addInquiry from "Menu/addInquiry.vue";
import { LAYOUTS_SYMBOL, PAGE_APP_SYMBOL, LOC_PAGE_NAME_SYMBOL, PC_RES_SYMBOL } from "_Main/injectionSymbols";

function setUnsavedChanges(hasUnsavedChanges) {
    if (hasUnsavedChanges) {
        if (!document.title.startsWith("*")) {
            document.title = "*" + document.title;
        }
        window.onbeforeunload = function () {
            return "This page has unsaved changes.";
        };
    } else {
        if (document.title.startsWith("*")) {
            document.title = document.title.slice(1);
        }
        window.onbeforeunload = null;
    }
}
function areThereUnsavedChanges() {
    return window.onbeforeunload != null;
}
function getFooterClientRect() {
    const footer = document.getElementById("root-page-footer-el");
    if (footer) {
        return footer.getBoundingClientRect();
    }
    return new DOMRect(0, 0, 0, 0);
}

export function createPcPage(
    vueRootApp,
    {
        ShowProgramSelector,
        ShowAgencySelector,
        Programs,
        CSRFToken,
        CSRFNonce,
        LoadClientModelParameters,
        LoggedInUser,
        LoggedInRole,
        ProgramInfo,
        BreadcrumbModel,
        ProjectInquiriesController,
        NotificationController,
        HomeUrl,
        LoginUrl,
        InvalidSessionUrl,
        PreloadModel,
        BannerModel,
        NavbarBannerModel,
        LocalizedPageName,
        Navigation,
        AgencyLogoUrl,
        AvailableProgramsInfo,
        AddProgramsUrl,
        ProgramChangeUrl,
        COLSFrameSrc,
        EnforceMfaForCPRAdmins,
        ManageAccountUrl,

        // app properties
        ProgramId,
        ActionUrls,
        DownloadDocumentUrl,
        UploadFileUrl,
        ErrorReporterUrl,
        PrintStyling,
        DateCultureMessageInfo
    }
) {
    vueRootApp.provide(LOC_PAGE_NAME_SYMBOL, LocalizedPageName);
    const $res = vueRootApp.runWithContext(() => {
        return inject(PC_RES_SYMBOL);
    });

    function getXhrHeaders() {
        return {
            CSRFToken,
            CSRFNonce
        };
    }
    const $app = createPcApp(
        vueRootApp,
        {
            ProgramId,
            ActionUrls,
            DownloadDocumentUrl,
            UploadFileUrl,
            ErrorReporterUrl,
            PrintStyling,
            DateCultureMessageInfo
        },
        {
            getXhrHeaders,
            onInvalidSessionError() {
                window.location.replace(InvalidSessionUrl);
            },
            transformActionUrls: null,
            setUnsavedChanges
        }
    );
    const $serviceControllers = vueRootApp.config.globalProperties.$serviceControllers;
    $serviceControllers.ProjectInquiriesController = new ServiceController(ProjectInquiriesController, $app);
    $serviceControllers.NotificationController = new ServiceController(NotificationController, $app);

    const userData = mapLoggedInUserData(LoggedInUser, LoggedInRole);
    function loggedInUserHasPrivilege(privilege) {
        return userHasPrivilege(LoggedInRole, privilege);
    }
    function loggedInUserHasAdminPrivilege(privilege) {
        return userHasAdminPrivilege(LoggedInRole, privilege);
    }

    const isTestProgram = ProgramInfo && ProgramInfo.IsTestProgram;
    const isGlobalDataProgram = ProgramInfo && ProgramInfo.IsGlobalDataProgram;
    const COLS = createCOLS(COLSFrameSrc);
    const layoutBinder = computed(() => {
        return {
            programs: Programs,
            showProgramSelector: ShowProgramSelector,
            breadcrumbModel: BreadcrumbModel,
            loading: $app.loading.value,
            waiting: $app.waiting.value,
            waitMessage: $app.waitMessage.value
        };
    });

    const Model = ref(null);
    async function afterConnect({ skipAssignModelOnLoad, onLoaded, extendClientModelParameters }) {
        if ($app.LoadClientModel && LoadClientModelParameters) {
            if (extendClientModelParameters) {
                extendClientModelParameters(LoadClientModelParameters);
            }
            $app.loading.value = true;
            try {
                const result = await $app.LoadClientModel(LoadClientModelParameters);
                if (!skipAssignModelOnLoad) {
                    Model.value = result.Data;
                }

                if (onLoaded) {
                    onLoaded(result.Data);
                }
            } finally {
                $app.loading.value = false;
            }
        }
    }
    const showMfaPrompt = createShowMfaPrompt({ $res, $app, EnforceMfaForCPRAdmins, ManageAccountUrl, userData });

    function showQuestionDialog() {
        $app.showDialog({
            title: $serviceControllers.ProjectInquiriesController.SupportInfo.ButtonText,
            customComponent: addInquiry,
            large: true
        });
    }
    function closeQuestionDialog() {
        $app.closeDialog({
            resolve: true,
            resolveValue: null
        });
        GlobalEventBus.emit("inquiry_dialog_closed");
    }

    function hoistElementToGlobalScroll(element) {
        const pageLayout = vueRootApp.runWithContext(() => {
            return inject(LAYOUTS_SYMBOL).page;
        });
        pageLayout.hoistElementToGlobalScroll(element);
    }

    const { logout, navigateToReturnUrl, cancel } = createNavFunctions({
        $app,
        LoginUrl,
        getXhrHeaders,
        BreadcrumbModel,
        HomeUrl
    });

    const pageApp = {
        ...$app,
        ...userData,
        loggedInUserHasPrivilege,
        loggedInUserHasAdminPrivilege,
        isTestProgram,
        isGlobalDataProgram,
        ...COLS,
        layoutBinder,
        Model,
        afterConnect,
        getXhrHeaders,
        showMfaPrompt,
        showQuestionDialog,
        closeQuestionDialog,
        setUnsavedChanges,
        areThereUnsavedChanges,
        hoistElementToGlobalScroll,
        getFooterClientRect,
        logout,
        navigateToReturnUrl,
        cancel,
        ...LOCATION_UTIL,

        ShowProgramSelector,
        ShowAgencySelector,
        Programs,
        CSRFToken,
        CSRFNonce,
        LoadClientModelParameters,
        LoggedInUser,
        LoggedInRole,
        ProgramInfo,
        BreadcrumbModel,
        ProjectInquiriesController,
        NotificationController,
        HomeUrl,
        LoginUrl,
        InvalidSessionUrl,
        PreloadModel,
        BannerModel,
        NavbarBannerModel,
        LocalizedPageName,
        Navigation,
        AgencyLogoUrl,
        AvailableProgramsInfo,
        AddProgramsUrl,
        ProgramChangeUrl,
        COLSFrameSrc,
        EnforceMfaForCPRAdmins,
        ManageAccountUrl,

        // app properties
        ProgramId,
        ActionUrls,
        DownloadDocumentUrl,
        UploadFileUrl,
        ErrorReporterUrl,
        PrintStyling
    };

    vueRootApp.provide(PAGE_APP_SYMBOL, pageApp);
    return pageApp;
}

export function usePageApp({ skipAssignModelOnLoad, onLoaded, extendClientModelParameters }) {
    const page = inject(PAGE_APP_SYMBOL);
    page.afterConnect({ skipAssignModelOnLoad, onLoaded, extendClientModelParameters });
    onMounted(() => {
        if (page.COLSframe) {
            // need to wait until this component is mounted so that we have a ref to the $el
            window.document.body.appendChild(page.COLSframe);
        }
        page.showMfaPrompt();
    });
    return page;
}

function createNavFunctions({ $app, LoginUrl, getXhrHeaders, BreadcrumbModel, HomeUrl }) {
    async function logout() {
        const loginUrl = LoginUrl;
        const url = loginUrl + "/LogOut";
        const logoutAction = $app.createActionMethod(url, getXhrHeaders());
        const result = await logoutAction({});

        if (result.Data.IsSSO) {
            window.close();
        }
        window.location.replace(loginUrl);
    }

    // As breadcrumb Model has last entry as page itself so second last index
    // shows where it is coming from and hence the return url
    const returnIndex = BreadcrumbModel != null ? BreadcrumbModel.Entries.length - 2 : -1;
    const returnUrl =
        returnIndex >= 0 && BreadcrumbModel.Entries[returnIndex].Url != null
            ? BreadcrumbModel.Entries[returnIndex].Url
            : HomeUrl;
    function navigateToReturnUrl() {
        LOCATION_UTIL.replaceHref(returnUrl);
    }
    function cancel() {
        setUnsavedChanges(false);
        navigateToReturnUrl();
    }
    return {
        logout,
        navigateToReturnUrl,
        cancel
    };
}

function createShowMfaPrompt({ $res, $app, EnforceMfaForCPRAdmins, ManageAccountUrl, userData }) {
    const enableMfaDialogCloseBtnLabel = $res({
        namespace: enableMfADialogNameSpace,
        key: enableMfADialogKeys.enableMfaDialogCloseBtnLabel
    });
    const enableMfaDialogGoToMyAccountBtnLabel = $res({
        namespace: enableMfADialogNameSpace,
        key: enableMfADialogKeys.enableMfaDialogGoToMyAccountBtnLabel
    });
    const enableMfaDialogMessage = $res({
        namespace: enableMfADialogNameSpace,
        key: enableMfADialogKeys.enableMfaDialogMessage
    });
    const enableMfaDialogTitle = $res({
        namespace: enableMfADialogNameSpace,
        key: enableMfADialogKeys.enableMfaDialogTitle
    });

    async function showMfaPrompt() {
        if (EnforceMfaForCPRAdmins && userData.loggedInUserIsCPAdmin && !userData.loggedInUserHasMfa) {
            const closeResult = "CLOSE";
            const gotToMyAccountResult = "GO_TO_MY_ACCOUNT";
            const dialogResult = await $app.showDialog({
                title: enableMfaDialogTitle,
                message: enableMfaDialogMessage,
                buttons: [
                    {
                        text: enableMfaDialogCloseBtnLabel,
                        result: closeResult
                    },
                    {
                        text: enableMfaDialogGoToMyAccountBtnLabel,
                        primary: true,
                        result: gotToMyAccountResult
                    }
                ]
            });

            if (dialogResult === gotToMyAccountResult) {
                LOCATION_UTIL.setHref(ManageAccountUrl);
            }
        }
    }
    return showMfaPrompt;
}
